'use client';

import { useState } from 'react';
// Deep import, not the '@aegis/shared' barrel: the barrel pulls zod, the
// action registry and the context-variable registry into the client bundle,
// which tripled this page's JS. Same style as the existing deep import in
// components/agents/skill-editor.tsx.
import { analyzeEmailHeaders, type HeaderAnalysis } from '@aegis/shared/src/utils/email-header-analyzer';
import { AlertCircle, CheckCircle2, Lock, XCircle } from 'lucide-react';

/**
 * Header analysis runs entirely in the browser: analyzeEmailHeaders is a pure
 * function from @aegis/shared, so the pasted headers — which contain the
 * user's real correspondents, subjects and routing — never reach our servers.
 */
export function HeaderAnalyzerTool() {
  const [raw, setRaw] = useState('');
  const [result, setResult] = useState<HeaderAnalysis | null>(null);

  function analyze(e: React.FormEvent) {
    e.preventDefault();
    setResult(analyzeEmailHeaders(raw));
  }

  return (
    <div>
      <p className="mb-4 flex items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-2 text-xs font-medium text-primary">
        <Lock className="h-3.5 w-3.5" />
        Analyzed in your browser. Your headers are never sent to us.
      </p>

      <form onSubmit={analyze}>
        <label htmlFor="raw-headers" className="text-sm font-medium">
          Paste the raw message headers
        </label>
        <textarea
          id="raw-headers"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={12}
          spellCheck={false}
          placeholder={'Received: from ...\nAuthentication-Results: ...\nFrom: ...'}
          className="mt-2 w-full rounded-lg border border-border bg-background p-3 font-mono text-xs outline-none focus:border-primary"
        />
        <button
          type="submit"
          disabled={!raw.trim()}
          className="mt-3 rounded-lg bg-primary px-6 py-2.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
        >
          Analyze
        </button>
      </form>

      {result && (
        <div className="mt-8 space-y-6">
          <div className="grid gap-3 sm:grid-cols-3">
            <AuthCard label="SPF" result={result.spf?.result} detail={result.spf?.domain} aligned={result.alignment.spf_aligned} />
            <AuthCard label="DKIM" result={result.dkim?.result} detail={result.dkim?.domain} aligned={result.alignment.dkim_aligned} />
            <AuthCard label="DMARC" result={result.dmarc?.result} detail={result.dmarc?.policy ? `p=${result.dmarc.policy}` : undefined} />
          </div>

          {result.warnings.length > 0 && (
            <ul className="space-y-2">
              {result.warnings.map((warning) => (
                <li key={warning} className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 text-sm">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                  <span className="text-muted-foreground">{warning}</span>
                </li>
              ))}
            </ul>
          )}

          {result.hops.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold">
                Delivery path · {result.total_delivery_sec}s total
              </h2>
              <ol className="mt-3 space-y-2">
                {result.hops.map((hop) => (
                  <li key={hop.index} className="rounded-lg border border-border bg-card p-3 text-xs">
                    <span className="font-mono break-all">
                      {hop.from ?? 'unknown'} → {hop.by ?? 'unknown'}
                    </span>
                    <span className="ml-2 text-muted-foreground">
                      {hop.delay_sec > 0 ? `+${hop.delay_sec}s` : ''}
                      {hop.with ? ` · ${hop.with}` : ''}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AuthCard({
  label,
  result,
  detail,
  aligned,
}: {
  label: string;
  result?: string;
  detail?: string;
  aligned?: boolean;
}) {
  const passed = result === 'pass';
  const Icon = passed ? CheckCircle2 : XCircle;
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2">
        <Icon
          className={`h-4 w-4 ${passed ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}
          aria-hidden
        />
        <span className="text-sm font-medium">{label}</span>
        <span className="ml-auto text-xs uppercase text-muted-foreground">{result ?? 'none'}</span>
      </div>
      {detail && <p className="mt-1.5 break-all text-xs text-muted-foreground">{detail}</p>}
      {aligned !== undefined && (
        <p className={`mt-1.5 text-xs ${aligned ? 'text-muted-foreground' : 'text-amber-600 dark:text-amber-400'}`}>
          {aligned ? 'Aligned with From:' : 'Not aligned with From: — does not satisfy DMARC'}
        </p>
      )}
    </div>
  );
}
